
import React from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { addLab, Lab } from "@/services/universityService";

interface LabFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (lab: Lab) => void;
  universityId: string | null;
}

interface LabFormValues {
  name: string;
}

const LabForm: React.FC<LabFormProps> = ({ isOpen, onClose, onSuccess, universityId }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  
  const form = useForm<LabFormValues>({
    defaultValues: {
      name: "",
    }
  });
  
  const handleAddLab = async (data: LabFormValues) => {
    if (!universityId) {
      toast.error("Please select a university first");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const labData = {
        name: data.name,
        university_id: universityId
      };
      
      const newLab = await addLab(labData);
      
      if (newLab) {
        toast.success("Lab added successfully");
        onSuccess(newLab);
        form.reset();
        onClose();
      } else {
        toast.error("Failed to add lab");
      }
    } catch (error) {
      console.error("Error adding lab:", error);
      toast.error("An error occurred while adding the lab");
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Lab</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(handleAddLab)} className="space-y-4">
          <div>
            <Label htmlFor="lab-name">Lab Name</Label>
            <Input id="lab-name" placeholder="Lab name" {...form.register("name")} required />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Adding..." : "Add Lab"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LabForm;
